import type { BulkTelegramGroupOutcome, BulkTelegramGroupResult } from './types';

/**
 * `code` values the UI branches on (force-resubmit, rate-limit retry) —
 * confirmed against the assistant-api backend source (`telegram-groups.service.ts`)
 * in PR #3224 review: each is the fully-qualified `telegram-group.exceptions.*`
 * i18n key shown here, carried verbatim on both the WS `JobFailed` frame and
 * the bulk per-row `JobStatus.returnValue`. Never compare against a bare
 * literal (e.g. `'FLOOD_WAIT'`) — always import these constants. Shared
 * between the bulk per-row result parsed here (SKY-4484) and the single-group
 * `JobFailed` WS frame's own `code` field (SKY-4411) — same codes, two
 * different transports. `bulk-result.test.ts` pins every one against its
 * backend key — a wrong string fails silently, falling through to the generic
 * failure toast with no other signal.
 */
export const ARCHIVE_REQUIRES_FORCE_CODE = 'telegram-group.exceptions.ARCHIVE_REQUIRES_FORCE';
/**
 * The group is not on Telegram any more — deleted there, or our account was
 * removed from it — so the peer does not resolve at all and there is nothing
 * left to close (assistant#316, SKY-4569).
 *
 * Recovery is identical to `ARCHIVE_REQUIRES_FORCE`: only `force: true` can
 * archive the record. The backend splits the two because the *explanations*
 * differ — ARCHIVE_REQUIRES_FORCE tells the operator an admin still in the
 * group can close it by hand, which is impossible advice for a group that no
 * longer exists — so any UI branching on "offer a force archive" must accept
 * BOTH codes, or a deleted group is left with no way out of the dashboard.
 */
export const ARCHIVE_PEER_UNREACHABLE_CODE = 'telegram-group.exceptions.ARCHIVE_PEER_UNREACHABLE';
export const ARCHIVE_NOT_CONFIRMED_CODE = 'telegram-group.exceptions.ARCHIVE_NOT_CONFIRMED';

/**
 * Every archive failure whose only recovery is a `force: true` re-submit.
 *
 * One list, because the alternative was tried and failed: when
 * `ARCHIVE_PEER_UNREACHABLE` was added, "offer the force path for these codes"
 * lived as inline `a || b` guards in two components plus the key types of two
 * copy maps, and every one of them silently went on meaning "the old code
 * only". A deleted group reached the dashboard with no way to archive it — and
 * so no way to delete it either, the backend allowing a delete only for an
 * archived record (SKY-4569).
 *
 * Branch with {@link isArchiveForceCode} instead of comparing codes, and key
 * any per-code table on {@link ArchiveForceCode}: a new member then breaks the
 * build until its copy exists, rather than quietly losing its recovery.
 */
export const ARCHIVE_FORCE_CODES = [
  ARCHIVE_REQUIRES_FORCE_CODE,
  ARCHIVE_PEER_UNREACHABLE_CODE,
] as const;

/** A member of {@link ARCHIVE_FORCE_CODES}, for exhaustive per-code tables. */
export type ArchiveForceCode = (typeof ARCHIVE_FORCE_CODES)[number];

const ARCHIVE_FORCE_CODE_SET: ReadonlySet<string> = new Set(ARCHIVE_FORCE_CODES);

/**
 * Whether this failure is recovered by re-submitting the archive with
 * `force: true`. Narrows, so a caller can index a per-code table right after.
 */
export const isArchiveForceCode = (code: string | undefined): code is ArchiveForceCode =>
  code !== undefined && ARCHIVE_FORCE_CODE_SET.has(code);
export const FLOOD_WAIT_CODE = 'telegram-group.exceptions.FLOOD_WAIT';
/**
 * `create` job failure code for a hand-typed `botUsername` that is shaped
 * like a bot (passes the `/^skynet_[a-z0-9_]+_bot$/` HTTP-edge check) but
 * doesn't actually exist — only the worker can tell, so a well-formed but
 * unknown bot gives a 202 followed by this failed job, not a 400 on the
 * request (SKY-4521). Picking from the trading-bots list never hits this.
 */
export const BAD_REQUEST_BOT_USERNAME_CODE = 'telegram-group.exceptions.BAD_REQUEST_BOT_USERNAME';

/**
 * `change-bot` job failure codes (SKY-4526) — each leaves the group in a
 * different state, so the UI branches on these rather than showing a generic
 * "failed" message:
 * - `CHANGE_BOT_NOT_A_MEMBER`: nothing touched; our account left the group, so
 *   only an admin still in it can swap the bot by hand.
 * - `CHANGE_BOT_INVITE_FAILED`: the group is left with **no bot at all** (the
 *   old one was already kicked) — Telegram refused the new one, usually its
 *   privacy settings. The one case that deserves a more prominent warning.
 * - `CHANGE_BOT_NOT_CONFIRMED`: the new bot is in the group but never
 *   confirmed `/start`. The worker operation is idempotent, so retrying with
 *   the same bot re-sends `/start` and is the exact remedy.
 */
export const CHANGE_BOT_NOT_A_MEMBER_CODE = 'telegram-group.exceptions.CHANGE_BOT_NOT_A_MEMBER';
export const CHANGE_BOT_INVITE_FAILED_CODE = 'telegram-group.exceptions.CHANGE_BOT_INVITE_FAILED';
export const CHANGE_BOT_NOT_CONFIRMED_CODE = 'telegram-group.exceptions.CHANGE_BOT_NOT_CONFIRMED';

/**
 * `update` job failure codes specific to a rename (SKY-4546) — the worker
 * runs the same create-time name validation the create form does, so a name
 * that would be rejected at creation fails identically here. Nothing is
 * persisted on any of these: the worker renames on Telegram first and only
 * writes the row on success, reverting the old title on Telegram if the write
 * then fails — so resubmitting the exact same rename is always safe, even
 * though a resubmit of one of the four `BAD_REQUEST_*` codes below can only
 * succeed if something about the group's own state (e.g. a conflicting
 * group's number) changed in the meantime.
 * - `BAD_REQUEST_GROUP_NAME`: doesn't match the Skynet naming scheme (or
 *   names two exchanges without a DL/DLA marker).
 * - `BAD_REQUEST_GROUP_NUMBER`: the name carries no valid group number.
 * - `BAD_REQUEST_EXCHANGE`: the name doesn't name a known exchange.
 * - `BAD_REQUEST_NOT_UNIQUE`: another group already owns that group number —
 *   renaming can move a group's number, and this fires on a collision.
 * - `TELEGRAM_ERROR`: Telegram refused the rename (e.g. we lost rename rights
 *   in the group).
 * - `NOT_FOUND`: the group is gone — no dashboard action can recover this.
 */
export const BAD_REQUEST_GROUP_NAME_CODE = 'telegram-group.exceptions.BAD_REQUEST_GROUP_NAME';
export const BAD_REQUEST_GROUP_NUMBER_CODE = 'telegram-group.exceptions.BAD_REQUEST_GROUP_NUMBER';
export const BAD_REQUEST_EXCHANGE_CODE = 'telegram-group.exceptions.BAD_REQUEST_EXCHANGE';
export const BAD_REQUEST_NOT_UNIQUE_CODE = 'telegram-group.exceptions.BAD_REQUEST_NOT_UNIQUE';
export const TELEGRAM_ERROR_CODE = 'telegram-group.exceptions.TELEGRAM_ERROR';
export const NOT_FOUND_CODE = 'telegram-group.exceptions.NOT_FOUND';

const isBulkTelegramGroupOutcome = (value: unknown): value is BulkTelegramGroupOutcome =>
  value === 'archived' || value === 'deleted' || value === 'skipped' || value === 'failed';

/** A field that, when present at all, must be of the given type — absent is fine. */
const isOptional = (value: unknown, type: 'number' | 'object' | 'string'): boolean =>
  value === undefined ||
  (type === 'object' ? typeof value === type && value !== null : typeof value === type);

const isBulkTelegramGroupResult = (value: unknown): value is BulkTelegramGroupResult =>
  typeof value === 'object' &&
  value !== null &&
  'id' in value &&
  typeof value.id === 'number' &&
  'outcome' in value &&
  isBulkTelegramGroupOutcome(value.outcome) &&
  (!('title' in value) || isOptional(value.title, 'string')) &&
  (!('code' in value) || isOptional(value.code, 'string')) &&
  (!('message' in value) || isOptional(value.message, 'string')) &&
  (!('retryAfterSeconds' in value) || isOptional(value.retryAfterSeconds, 'number')) &&
  (!('args' in value) || isOptional(value.args, 'object'));

/**
 * Validates `GetTelegramGroupJobResponse.returnValue` — typed `unknown` on
 * the wire (BullMQ's return value is genuinely arbitrary) — into a
 * `BulkTelegramGroupResult[]`, or `null` if it isn't shaped like one. Mirrors
 * `@skynet/data-ws-core`'s `isJobMessage`/`isJobProgressPayload` style:
 * explicit field checks rather than a blind cast.
 */
export const parseBulkTelegramGroupsResult = (value: unknown): BulkTelegramGroupResult[] | null =>
  Array.isArray(value) && value.every(isBulkTelegramGroupResult) ? value : null;
